import jsPDF from "jspdf";
import { supabase } from "../../lib/supabase";
import { logActivity } from "./driveClient";

// Musiklabel-typische Leistungsvorschläge — als Fallback wenn DB-Katalog leer ist
export const SERVICE_PRESETS = [
  { label: "Musikproduktion / Beat", unit: "Stück", price: 250 },
];

// DB-basierte Leistungen (editierbar)
export async function fetchServices() {
  const { data, error } = await supabase.from("drive_invoice_services").select("*").eq("is_active", true).order("sort_order").order("label");
  if (error) throw error;
  return data;
}
export async function createService(payload) {
  const { data, error } = await supabase.from("drive_invoice_services").insert([payload]).select().single();
  if (error) throw error;
  await logActivity("create_service", { type: "service", id: data.id, name: data.label });
  return data;
}
export async function updateService(id, patch) {
  const { data, error } = await supabase.from("drive_invoice_services").update({ ...patch, updated_at: new Date().toISOString() }).eq("id", id).select().single();
  if (error) throw error;
  return data;
}
export async function deleteService(id) {
  const { error } = await supabase.from("drive_invoice_services").delete().eq("id", id);
  if (error) throw error;
  await logActivity("delete_service", { type: "service", id });
}

// ═══════════════════════════════════════════════════════════════
// Settings CRUD (Singleton)
// ═══════════════════════════════════════════════════════════════
export async function fetchInvoiceSettings() {
  const { data, error } = await supabase.from("drive_invoice_settings").select("*").eq("singleton", true).single();
  if (error) throw error;
  return data;
}
export async function updateInvoiceSettings(patch) {
  const { data, error } = await supabase.from("drive_invoice_settings").update({ ...patch, updated_at: new Date().toISOString() }).eq("singleton", true).select().single();
  if (error) throw error;
  await logActivity("update_invoice_settings", { type: "invoice_settings", id: data.id });
  return data;
}

// ═══════════════════════════════════════════════════════════════
// Invoices CRUD
// ═══════════════════════════════════════════════════════════════
export async function fetchInvoices({ kind } = {}) {
  let q = supabase.from("drive_invoices").select("*, drive_artists(artist_name), drive_customers(name), drive_externals(full_name)").order("invoice_date", { ascending: false }).order("created_at", { ascending: false });
  if (kind) q = q.eq("kind", kind);
  const { data, error } = await q;
  if (error) throw error;
  return data;
}
export async function deleteInvoice(id) {
  const { data: inv } = await supabase.from("drive_invoices").select("storage_path, invoice_number").eq("id", id).single();
  if (inv?.storage_path) {
    await supabase.storage.from("drive-files").remove([inv.storage_path]).catch(() => {});
  }
  const { error } = await supabase.from("drive_invoices").delete().eq("id", id);
  if (error) throw error;
  await logActivity("delete_invoice", { type: "invoice", id, name: inv?.invoice_number });
}
export async function markInvoicePaid(id) {
  const { data, error } = await supabase.from("drive_invoices").update({ status: "paid", paid_at: new Date().toISOString(), updated_at: new Date().toISOString() }).eq("id", id).select().single();
  if (error) throw error;
  await logActivity("mark_invoice_paid", { type: "invoice", id, name: data.invoice_number });
  return data;
}

async function nextInvoiceNumber() {
  const { data, error } = await supabase.rpc("next_invoice_number");
  if (error) throw error;
  return data;
}

async function assignCustomerNumber(kind, id) {
  const { data, error } = await supabase.rpc("assign_customer_number", { target_kind: kind, target_id: id });
  if (error) throw error;
  return data;
}

// Berechne Summen aus Items
export function computeInvoiceTotals(items, taxRate) {
  const subtotal = items.reduce((sum, i) => sum + (Number(i.quantity) * Number(i.unit_price)), 0);
  const taxAmount = subtotal * (Number(taxRate) / 100);
  const total = subtotal + taxAmount;
  return {
    subtotal: Math.round(subtotal * 100) / 100,
    tax_amount: Math.round(taxAmount * 100) / 100,
    total: Math.round(total * 100) / 100,
  };
}

function buildRecipientSnapshot({ artist, customer, external }) {
  if (artist) {
    return {
      kind: "artist",
      customer_number: artist.customer_number,
      name: artist.artist_name || [artist.first_name, artist.last_name].filter(Boolean).join(" "),
      full_name: [artist.first_name, artist.last_name].filter(Boolean).join(" ") || artist.artist_name,
      email: artist.email,
      phone: artist.phone,
      address: artist.address,
      tax_id: artist.tax_id,
    };
  }
  if (customer) {
    return {
      kind: "customer",
      customer_number: customer.customer_number,
      name: customer.name,
      company: customer.company,
      email: customer.email,
      phone: customer.phone,
      address: customer.address,
    };
  }
  if (external) {
    return {
      kind: "external",
      customer_number: external.customer_number,
      name: external.full_name,
      relation: external.relation,
      email: external.email,
      phone: external.phone,
      address: external.address,
      tax_id: external.tax_id,
    };
  }
  return {};
}

// Erstellen einer Ausgangsrechnung
export async function createOutgoingInvoice({ artist_id, customer_id, external_id, invoice_date, service_date, due_date, items, tax_rate, notes, internal_notes }) {
  // 1) Kundennummer sicherstellen (idempotent)
  let recipient;
  if (artist_id) {
    await assignCustomerNumber("artist", artist_id);
    const { data } = await supabase.from("drive_artists").select("*").eq("id", artist_id).single();
    recipient = { artist: data };
  } else if (customer_id) {
    await assignCustomerNumber("customer", customer_id);
    const { data } = await supabase.from("drive_customers").select("*").eq("id", customer_id).single();
    recipient = { customer: data };
  } else if (external_id) {
    await assignCustomerNumber("external", external_id);
    const { data } = await supabase.from("drive_externals").select("*").eq("id", external_id).single();
    recipient = { external: data };
  }

  // 2) Absender-Snapshot
  const settings = await fetchInvoiceSettings();

  // 3) Rechnungsnummer
  const invoice_number = await nextInvoiceNumber();

  // 4) Summen
  const { subtotal, tax_amount, total } = computeInvoiceTotals(items, tax_rate);

  // 5) DB-Eintrag
  const recipientSnapshot = buildRecipientSnapshot(recipient);
  const now = new Date();
  const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;

  const { data: invoice, error } = await supabase.from("drive_invoices").insert([{
    invoice_number,
    kind: "ausgang",
    status: "draft",
    artist_id: artist_id || null,
    customer_id: customer_id || null,
    external_id: external_id || null,
    invoice_date: invoice_date || new Date().toISOString().slice(0, 10),
    service_date: service_date || null,
    due_date: due_date || null,
    sender_snapshot: settings,
    recipient_snapshot: recipientSnapshot,
    items,
    subtotal, tax_rate, tax_amount, total,
    notes: notes || null,
    internal_notes: internal_notes || null,
    contract_month: month,
  }]).select().single();
  if (error) throw error;

  // 6) PDF bauen und hochladen
  const pdf = await renderInvoicePdf(invoice);
  const blob = pdf.output("blob");
  const filename = `Rechnung-${invoice_number}.pdf`;
  const scopeId = artist_id || customer_id || external_id || "misc";
  const storagePath = `invoices/${scopeId}/${Date.now()}-${filename}`;
  const { error: upErr } = await supabase.storage.from("drive-files").upload(storagePath, blob, { contentType: "application/pdf", upsert: false });
  if (upErr) throw upErr;

  // 7) storage_path speichern
  await supabase.from("drive_invoices").update({ storage_path: storagePath }).eq("id", invoice.id);

  await logActivity("create_invoice", { type: "invoice", id: invoice.id, name: invoice_number });

  // 8) Client-Download
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);

  return { invoice: { ...invoice, storage_path: storagePath } };
}

// Erstellen einer Eingangsrechnung (manuell mit Uploadfile)
export async function createIncomingInvoice({ file, invoice_number, invoice_date, notes, artist_id, customer_id, external_id, total, tax_amount, subtotal }) {
  const settings = await fetchInvoiceSettings();
  const scopeId = artist_id || customer_id || external_id || "misc";
  const storagePath = `invoices-eingang/${scopeId}/${Date.now()}-${file.name}`;
  const { error: upErr } = await supabase.storage.from("drive-files").upload(storagePath, file, { contentType: file.type });
  if (upErr) throw upErr;

  const now = new Date();
  const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;

  const { data: invoice, error } = await supabase.from("drive_invoices").insert([{
    invoice_number,
    kind: "eingang",
    status: "draft",
    artist_id: artist_id || null,
    customer_id: customer_id || null,
    external_id: external_id || null,
    invoice_date: invoice_date || new Date().toISOString().slice(0, 10),
    sender_snapshot: settings,
    recipient_snapshot: {},
    items: [],
    subtotal: subtotal || 0,
    tax_rate: 0,
    tax_amount: tax_amount || 0,
    total: total || 0,
    notes,
    contract_month: month,
    storage_path: storagePath,
  }]).select().single();
  if (error) throw error;
  await logActivity("upload_invoice", { type: "invoice_in", id: invoice.id, name: invoice_number });
  return invoice;
}

// PDF-Header-Bild (STRAßENWERT + INDEPENDENT MUSIC LABEL)
const INVOICE_LOGO_URL = "https://customer-assets-jai6qajn.emergentagent.net/job_neon-beats-label/artifacts/as4oc6zo_INDEPENDENT_MUSIC_LABEL__1_-removebg-preview.png";

async function loadLogoDataUrl() {
  try {
    const resp = await fetch(INVOICE_LOGO_URL, { mode: "cors" });
    const blob = await resp.blob();
    return await new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.onerror = rej;
      r.readAsDataURL(blob);
    });
  } catch { return null; }
}

// ═══════════════════════════════════════════════════════════════
// PDF-Rendering für Rechnungen
// Seriös, klar, mit dezentem Bordeaux-Header.
// ═══════════════════════════════════════════════════════════════
export async function renderInvoicePdf(invoice) {
  const pdf = new jsPDF({ unit: "mm", format: "a4" });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const marginX = 20;
  const bannerH = 40;                 // Höhe der Header-Zone (Logo darauf, kein Balken)
  const marginBottom = 28;
  const contentW = pageW - 2 * marginX;

  const INK = [24, 24, 28];
  const MUTED = [110, 110, 118];
  const LINE = [220, 220, 224];
  const BORDEAUX = [128, 24, 38];
  const ACCENT = [180, 30, 42];
  const SOFT = [245, 245, 247];

  const logoData = await loadLogoDataUrl();
  const s = invoice.sender_snapshot || {};
  const r = invoice.recipient_snapshot || {};

  // ── Bordeaux-Banner mit Logo mittig ─────────────────────────
  pdf.setFillColor(...BORDEAUX);
  pdf.rect(0, 0, pageW, bannerH, "F");
  if (logoData) {
    try {
      const lw = 76, lh = 30;
      pdf.addImage(logoData, "PNG", (pageW - lw) / 2, (bannerH - lh) / 2, lw, lh);
    } catch (e) { /* ignore */ }
  }

  let y = bannerH + 14;

  // ── „RECHNUNG" links + Nummer ───────────────────────────────
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(22);
  pdf.setCharSpace(1);
  pdf.setTextColor(...INK);
  pdf.text("RECHNUNG", marginX, y);
  pdf.setCharSpace(0);

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);
  pdf.setTextColor(...MUTED);
  pdf.text(invoice.invoice_number || "", marginX, y + 5);

  // Rotes Akzent-Underline
  pdf.setDrawColor(...ACCENT);
  pdf.setLineWidth(0.8);
  pdf.line(marginX, y + 2, marginX + 36, y + 2);
  pdf.setLineWidth(0.2);

  // Absender (rechts, klein)
  pdf.setFontSize(7.5);
  pdf.setTextColor(...MUTED);
  const senderLines = [
    s.company_name || "Straßenwert Records",
    s.owner_name,
    s.address_street,
    [s.address_zip, s.address_city].filter(Boolean).join(" "),
  ].filter(Boolean);
  senderLines.forEach((ln, i) => pdf.text(ln, pageW - marginX, y - 4 + i * 3.5, { align: "right" }));

  y += 18;

  // ── Kleiner Briefkopf-Absender ÜBER dem Empfänger ──────────
  y += 4;
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(7);
  pdf.setTextColor(...MUTED);
  const briefkopf = [s.company_name, s.address_street, `${s.address_zip || ""} ${s.address_city || ""}`.trim()].filter(Boolean).join(" | ");
  pdf.text(briefkopf, marginX, y);
  // Unterstrich als Briefkopf-Linie
  pdf.setDrawColor(...LINE);
  pdf.setLineWidth(0.2);
  pdf.line(marginX, y + 2, marginX + 90, y + 2);
  y += 8;

  // ── Empfänger (links) + Rechnungsdaten (rechts) ────────────
  const blockY = y;
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(11);
  pdf.setTextColor(...INK);
  // Bürgerlicher Name (full_name) bevorzugt, dann name als Fallback
  const recipientName = r.full_name || r.name || "—";
  pdf.text(recipientName, marginX, y);
  y += 5;
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9.5);
  if (r.company) { pdf.text(r.company, marginX, y); y += 4.5; }
  if (r.address) {
    const addrLines = pdf.splitTextToSize(r.address, 80);
    for (const l of addrLines) { pdf.text(l, marginX, y); y += 4.5; }
  }
  if (r.email) { pdf.setTextColor(...MUTED); pdf.text(r.email, marginX, y); y += 4.5; }

  const infoX = pageW - marginX - 68;
  let infoY = blockY;
  const infoRow = (label, val) => {
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.setTextColor(...MUTED);
    pdf.text(label, infoX, infoY);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9.5);
    pdf.setTextColor(...INK);
    pdf.text(val || "—", pageW - marginX, infoY, { align: "right" });
    infoY += 5.5;
  };
  infoRow("Rechnungsdatum", new Date(invoice.invoice_date).toLocaleDateString("de-DE"));
  if (invoice.service_date) infoRow("Leistungsdatum", new Date(invoice.service_date).toLocaleDateString("de-DE"));
  if (r.customer_number) infoRow("Kunden-Nr.", r.customer_number);
  if (invoice.due_date) infoRow("Fällig am", new Date(invoice.due_date).toLocaleDateString("de-DE"));

  y = Math.max(y, infoY) + 8;

  // ── Optionale Anmerkung ─────────────────────────────────────
  if (invoice.notes) {
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9.5);
    pdf.setTextColor(...INK);
    const nlines = pdf.splitTextToSize(invoice.notes, contentW);
    for (const l of nlines) { pdf.text(l, marginX, y); y += 5; }
    y += 4;
  }

  // ── Positions-Tabelle ─────────────────────────────────────
  const colX = { pos: marginX, desc: marginX + 12, qty: pageW - marginX - 62, unit: pageW - marginX - 42, price: pageW - marginX - 22, total: pageW - marginX };
  pdf.setFillColor(...SOFT);
  pdf.rect(marginX, y - 4, contentW, 8, "F");
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(8);
  pdf.setTextColor(...INK);
  pdf.text("POS", colX.pos, y);
  pdf.text("BESCHREIBUNG", colX.desc, y);
  pdf.text("MENGE", colX.qty, y, { align: "right" });
  pdf.text("EH", colX.unit, y, { align: "right" });
  pdf.text("EINZEL", colX.price, y, { align: "right" });
  pdf.text("GESAMT", colX.total, y, { align: "right" });
  y += 6;

  const ensureRoom = (need) => {
    if (y + need > pageH - marginBottom) { pdf.addPage(); y = bannerH + 14; }
  };

  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9.5);
  pdf.setTextColor(...INK);
  const items = invoice.items || [];
  items.forEach((it, i) => {
    const line = Number(it.quantity) * Number(it.unit_price);
    const descLines = pdf.splitTextToSize(it.description || "", 90);
    const rowH = Math.max(6, descLines.length * 4.8);
    ensureRoom(rowH + 3);
    pdf.setDrawColor(...LINE);
    pdf.setLineWidth(0.15);
    pdf.line(marginX, y - 3.5, pageW - marginX, y - 3.5);
    pdf.text(String(i + 1), colX.pos, y);
    for (let j = 0; j < descLines.length; j++) pdf.text(descLines[j], colX.desc, y + j * 4.8);
    pdf.text(String(it.quantity), colX.qty, y, { align: "right" });
    pdf.text(it.unit || "", colX.unit, y, { align: "right" });
    pdf.text(formatCurrency(it.unit_price), colX.price, y, { align: "right" });
    pdf.setFont("helvetica", "bold");
    pdf.text(formatCurrency(line), colX.total, y, { align: "right" });
    pdf.setFont("helvetica", "normal");
    y += rowH + 1.5;
  });

  pdf.setDrawColor(...LINE);
  pdf.setLineWidth(0.3);
  pdf.line(marginX, y, pageW - marginX, y);
  y += 6;

  // ── Summen-Block ───────────────────────────────────────────
  ensureRoom(30);
  const sumX = pageW - marginX - 60;
  const sumRow = (label, val, bold = false, accent = false) => {
    pdf.setFont("helvetica", bold ? "bold" : "normal");
    pdf.setFontSize(bold ? 10.5 : 9.5);
    pdf.setTextColor(...(accent ? ACCENT : MUTED));
    pdf.text(label, sumX, y);
    pdf.setTextColor(...(accent ? ACCENT : INK));
    pdf.text(val, pageW - marginX, y, { align: "right" });
    y += bold ? 7 : 5.5;
  };
  sumRow("Zwischensumme", formatCurrency(invoice.subtotal));
  if (Number(invoice.tax_rate) > 0) {
    sumRow(`USt. ${Number(invoice.tax_rate).toFixed(0)} %`, formatCurrency(invoice.tax_amount));
  } else if (s.small_business) {
    pdf.setFont("helvetica", "italic");
    pdf.setFontSize(8);
    pdf.setTextColor(...MUTED);
    pdf.text("Gemäß §19 UStG wird keine Umsatzsteuer ausgewiesen.", marginX, y);
    y += 5;
  }
  pdf.setDrawColor(...INK);
  pdf.setLineWidth(0.5);
  pdf.line(sumX, y - 1.5, pageW - marginX, y - 1.5);
  y += 2;
  sumRow("GESAMTBETRAG", formatCurrency(invoice.total), true, true);

  // ── Zahlungshinweis ──────────────────────────────────────
  ensureRoom(30);
  y += 4;
  if (s.invoice_footer) {
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(...INK);
    const footTextLines = pdf.splitTextToSize(s.invoice_footer || "", contentW);
    for (const l of footTextLines) { ensureRoom(5); pdf.text(l, marginX, y); y += 5; }
  }

  // ── Fußzeile ───────────────────────────────────────────
  const drawFooter = (page, total) => {
    const fy = pageH - 18;
    pdf.setDrawColor(...LINE);
    pdf.setLineWidth(0.25);
    pdf.line(marginX, fy - 6, pageW - marginX, fy - 6);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(7);
    pdf.setTextColor(...MUTED);

    const cols = [
      [s.company_name, s.owner_name, s.address_street, `${s.address_zip || ""} ${s.address_city || ""}`.trim()].filter(Boolean),
      [s.email, s.phone, s.website].filter(Boolean),
      [
        s.tax_id ? `St.-Nr.: ${s.tax_id}` : null,
        s.vat_id ? `USt-IdNr.: ${s.vat_id}` : null,
        s.small_business ? "Kleinunternehmer §19 UStG" : null,
      ].filter(Boolean),
      [s.bank_name, s.iban ? `IBAN: ${s.iban}` : null, s.bic ? `BIC: ${s.bic}` : null].filter(Boolean),
    ];

    const cw = contentW / 4;
    cols.forEach((lines, i) => {
      const x = marginX + cw * i;
      lines.forEach((ln, j) => {
        pdf.text(ln, x, fy + j * 3.5);
      });
    });

    pdf.setFontSize(6.5);
    pdf.text(`Seite ${page} / ${total}`, pageW - marginX, pageH - 6, { align: "right" });
  };

  const total = pdf.internal.getNumberOfPages();
  for (let i = 1; i <= total; i++) { pdf.setPage(i); drawFooter(i, total); }
  return pdf;
}

function formatCurrency(v) {
  return (Number(v || 0)).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
}
